import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddStandardDispositionModal, type DispositionOption } from '@/components/modals/AddStandardDispositionModal';
import { AddCustomDispositionModal } from '@/components/modals/AddCustomDispositionModal';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue } from '@/utils/legacy-utils';
import { getOfficerPluralLabel } from '@/utils/auth';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const sortCallDispositionList = _wg('sortCallDispositionList');
const indexOfValueInArrayByProperty = _wg('indexOfValueInArrayByProperty');

const tabs = [
  { id: 'showSettingsTab', label: 'Settings' },
  { id: 'showCallDispositionManagementTab', label: 'Dispositions' },
];

const dispositionColumns: KstDataGridColumn[] = [
  { field: 'dispositionCode', name: 'Disposition Code', size: 'hidden' },
  {
    field: 'dispositionValue',
    name: 'Disposition',
    size: 'col-xs-10',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.dispositionValue)) {
        return e.dispositionValue;
      }
      return 'Error getting Disposition.';
    },
  },
];

const dispositionHeaderButtons = [
  { name: 'Add Standard', function: 'addstandard', src: '/Content/TourImages/add-r.svg' },
  { name: 'Add Custom', function: 'addcustom', src: '/Content/TourImages/add-r.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

export function CallCardManagementScreen() {
  const { theme } = useTheme();
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  const [activeTab, setActiveTab] = useState('showSettingsTab');
  const [activePage, setActivePage] = useState('showSettings');

  // Settings
  const [isPanicButtonEnabled, setIsPanicButtonEnabled] = useState<boolean | null>(null);
  const [isPriorityOnPdf, setIsPriorityOnPdf] = useState<boolean | null>(null);
  const [isShowingAllNatureOfCallOptionsOnSelfInitiate, setIsShowingAllNatureOfCallOptionsOnSelfInitiate] = useState<boolean | null>(null);

  // Loading states
  const [isDispositionsLoading, setIsDispositionsLoading] = useState<boolean>(false);

  // Disposition management entries
  const [dispositionEntries, setDispositionEntries] = useState<any[]>([]);
  const dispositionListCacheRef = useRef<any[] | null>(null);
  const globalDispositionListCacheRef = useRef<any[] | null>(null);

  // Stable ref to dispositionEntries for callbacks
  const dispositionEntriesRef = useRef(dispositionEntries);
  dispositionEntriesRef.current = dispositionEntries;

  // Add Standard modal
  const [addStandardModalOpen, setAddStandardModalOpen] = useState(false);
  const [addStandardLoading, setAddStandardLoading] = useState(false);
  const [availableStandardDispositions, setAvailableStandardDispositions] = useState<DispositionOption[]>([]);

  // Add Custom modal
  const [addCustomModalOpen, setAddCustomModalOpen] = useState(false);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Grid ref for scroll
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const dispositionRows: KstDataGridRows = useMemo(() => ({ entries: dispositionEntries }), [dispositionEntries]);

  // Cache helpers
  const getCompanyDispositionList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(dispositionListCacheRef.current)) {
        resolve(deepCopyProperties(dispositionListCacheRef.current));
      } else {
        api.listCompanyCallDispositions(
          (result: any) => {
            dispositionListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting nature of call data.', 'error');
          }
        );
      }
    });
  }, []);

  const getGlobalDispositionList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(globalDispositionListCacheRef.current)) {
        resolve(deepCopyProperties(globalDispositionListCacheRef.current));
      } else {
        api.listGlobalCallDispositions(
          (result: any) => {
            globalDispositionListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting standard nature of call data.', 'error');
          }
        );
      }
    });
  }, []);

  // Scroll-to-bottom: use a ref flag + useEffect so the scroll happens
  // after React has committed the new grid rows to the DOM.
  const shouldScrollToBottomRef = useRef(false);

  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false;
      const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    }
  }, [dispositionEntries]);

  const refreshManageDispositionList = useCallback(
    (doScrollToBottom = false) => {
      setIsDispositionsLoading(true);
      getCompanyDispositionList().then((value: any[]) => {
        sortCallDispositionList(value);
        for (let i = 0; i < value.length; i++) {
          value[i].listIdx = i + 1;
          value[i].isListEnabled = true;
        }
        if (doScrollToBottom) {
          shouldScrollToBottomRef.current = true;
        }
        setDispositionEntries(value);
        setIsDispositionsLoading(false);
      }).catch(() => {
        setIsDispositionsLoading(false);
      });
    },
    [getCompanyDispositionList]
  );

  const updateCallDispositions = useCallback(
    (entries: any[], doScrollToBottom = false) => {
      const dispositions = entries.map((elt: any) => ({
        dispositionCode: elt.dispositionCode,
        listIdx: elt.listIdx,
      }));
      api.updateCompanyCallDispositionList(
        { callDispositionList: dispositions },
        () => {
          dispositionListCacheRef.current = null;
          refreshManageDispositionList(doScrollToBottom);
        },
        () => {
          App.showPopupNotification('Failed to update call dispositions.', 'error');
        }
      );
    },
    [refreshManageDispositionList]
  );

  // Load settings on mount
  useEffect(() => {
    api.getCallCardSettings(
      (result: any) => {
        setIsPanicButtonEnabled(result.isPanicButtonEnabled);
        setIsPriorityOnPdf(result.isPriorityOnPdf);
        setIsShowingAllNatureOfCallOptionsOnSelfInitiate(result.isShowingAllNatureOfCallOptionsOnSelfInitiate);
      },
      () => {
        App.showPopupNotification('Error getting settings.', 'error');
      }
    );
  }, []);

  const handleUpdateSettings = useCallback(() => {
    api.updateCallCardSettings(
      { isPanicButtonEnabled, isPriorityOnPdf, isShowingAllNatureOfCallOptionsOnSelfInitiate },
      () => {
        App.showPopupNotification('Call settings saved.', 'success');
      },
      () => {
        App.showPopupNotification('Error updating settings.', 'error');
      }
    );
  }, [isPanicButtonEnabled, isPriorityOnPdf, isShowingAllNatureOfCallOptionsOnSelfInitiate]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'showSettingsTab') {
        setActivePage('showSettings');
      } else if (tabId === 'showCallDispositionManagementTab') {
        setActivePage('showCallDispositionManagement');
        dispositionListCacheRef.current = null;
        refreshManageDispositionList();
      }
    },
    [refreshManageDispositionList]
  );

  const handleButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'addstandard') {
        setAddStandardModalOpen(true);
        setAddStandardLoading(true);
        Promise.all([getCompanyDispositionList(), getGlobalDispositionList()]).then((values) => {
          const companyDisps = values[0] as any[];
          const globalDisps = values[1] as any[];
          const codeMap: Record<string, boolean> = companyDisps.reduce((result: any, item: any) => {
            result[item.dispositionCode] = true;
            return result;
          }, {});
          const available = globalDisps.filter((d: any) => !codeMap[d.dispositionCode.toString()]);
          setAvailableStandardDispositions(available);
          setAddStandardLoading(false);
        }).catch(() => {
          setAddStandardLoading(false);
        });
      } else if (functionName === 'addcustom') {
        setAddCustomModalOpen(true);
      } else if (functionName === 'delete') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Disposition(s) must be selected to delete.', 'warning');
          return;
        }
        const count = checkedItems.length;
        const message =
          count === 1
            ? 'Are you sure you want to delete this disposition?'
            : `Are you sure you want to delete ${count} dispositions?`;
        setDeleteConfirmItems(checkedItems);
        setDeleteConfirmMessage(message);
        setDeleteConfirmOpen(true);
      }
    },
    [getCompanyDispositionList, getGlobalDispositionList]
  );

  const handleAddStandardSave = useCallback(
    (data: { dispositionCodes: number[] }) => {
      const updated = [...dispositionEntriesRef.current];
      for (const dispositionCode of data.dispositionCodes) {
        updated.push({ dispositionCode, listIdx: null });
      }
      for (let i = 0; i < updated.length; i++) {
        updated[i].listIdx = i + 1;
        updated[i].isListEnabled = true;
      }
      setIsDispositionsLoading(true);
      updateCallDispositions(updated, true);
      setAddStandardModalOpen(false);
      setAddStandardLoading(false);
    },
    [updateCallDispositions]
  );

  const handleAddCustomSave = useCallback(
    (data: { dispositionValue: string }) => {
      api.addCustomCallDisposition(
        { dispositionValue: data.dispositionValue },
        () => {
          dispositionListCacheRef.current = null;
          refreshManageDispositionList(true);
          setAddCustomModalOpen(false);
        },
        () => {
          App.showPopupNotification('Failed to save disposition.', 'error');
        }
      );
    },
    [refreshManageDispositionList]
  );

  const handleConfirmDelete = useCallback(() => {
    const codeMap: Record<string, boolean> = {};
    deleteConfirmItems.forEach((item: any) => {
      codeMap[item.dispositionCode] = true;
    });
    const remaining = dispositionEntriesRef.current.filter(
      (entry: any) => !codeMap[entry.dispositionCode.toString()]
    );
    for (let i = 0; i < remaining.length; i++) {
      remaining[i].listIdx = i + 1;
      remaining[i].isListEnabled = true;
    }
    setDispositionEntries(remaining);
    updateCallDispositions(remaining);
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, [deleteConfirmItems, updateCallDispositions]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowMoved = useCallback(
    (row: any) => {
      const values = [...dispositionEntriesRef.current];
      const idx = values.findIndex((v: any) => v.dispositionCode === row.dispositionCode);
      if (idx >= 0) {
        values.splice(idx, 1);
      }
      values.splice(row.listIdx - 1, 0, row);
      for (let i = 0; i < values.length; i++) {
        values[i].listIdx = i + 1;
      }
      setDispositionEntries(values);
      updateCallDispositions(values);
    },
    [updateCallDispositions]
  );

  return (
    <div className={theme}>
      {/* Modals */}
      <AddStandardDispositionModal
        open={addStandardModalOpen}
        availableDispositions={availableStandardDispositions}
        loading={addStandardLoading}
        theme={theme}
        onSave={handleAddStandardSave}
        onCancel={() => setAddStandardModalOpen(false)}
      />

      <AddCustomDispositionModal
        open={addCustomModalOpen}
        theme={theme}
        onSave={handleAddCustomSave}
        onCancel={() => setAddCustomModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Disposition"
        message={deleteConfirmMessage}
        confirmText="Delete"
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showSettingsTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showSettingsTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Settings</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showCallDispositionManagementTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showCallDispositionManagementTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="8" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="8" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="8" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="3" y1="6" x2="3.01" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="3" y1="12" x2="3.01" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="3" y1="18" x2="3.01" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Dispositions</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Settings Page */}
            {activePage === 'showSettings' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Calls For Service Settings</h3>
                  <p className="kst-page-description">
                    Configure general settings for Calls for Service.
                  </p>
                </div>

                {isPriorityOnPdf !== null && isPanicButtonEnabled !== null && isShowingAllNatureOfCallOptionsOnSelfInitiate !== null ? (
                  <div className="kst-settings-form">
                    <div className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        id="chkIsPanicButtonEnabled"
                        className="kstc-checkbox"
                        checked={isPanicButtonEnabled === true}
                        onChange={(e) => setIsPanicButtonEnabled(e.target.checked)}
                      />
                      <label className="kstc-checkbox-label" htmlFor="chkIsPanicButtonEnabled">
                        Panic Button for Dispatch and Mobile {officerPluralLabel}
                      </label>
                    </div>
                    <div className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        id="chkIsPriorityOnPdf"
                        className="kstc-checkbox"
                        checked={isPriorityOnPdf === true}
                        onChange={(e) => setIsPriorityOnPdf(e.target.checked)}
                      />
                      <label className="kstc-checkbox-label" htmlFor="chkIsPriorityOnPdf">
                        Show "Priority" on PDF
                      </label>
                    </div>
                    <div className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        id="chkIsShowingAllNatureOfCallOptionsOnSelfInitiate"
                        className="kstc-checkbox"
                        checked={isShowingAllNatureOfCallOptionsOnSelfInitiate === true}
                        onChange={(e) => setIsShowingAllNatureOfCallOptionsOnSelfInitiate(e.target.checked)}
                      />
                      <label className="kstc-checkbox-label" htmlFor="chkIsShowingAllNatureOfCallOptionsOnSelfInitiate">
                        Show all Nature of Call options on Self Initiate
                      </label>
                    </div>
                    <div className="kst-settings-actions">
                      <button type="button" className="kstc-btn kstc-btn--primary" onClick={handleUpdateSettings}>
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="kst-loading"></div>
                )}
              </div>
            )}

            {/* Disposition Management Page */}
            {activePage === 'showCallDispositionManagement' && (
              <div className="kst-page" ref={gridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Disposition Management</h3>
                  <p className="kst-page-description">
                    Update Disposition items displayed on Calls for Service.<br />
                    You will need to log out and in to see the changes on Calls for Service.
                  </p>
                </div>

                <KstDataGrid
                  columns={dispositionColumns}
                  rows={dispositionRows}
                  headerbuttons={dispositionHeaderButtons}
                  showcheckbox={true}
                  showordercolumn={true}
                  mobiletitlefield="dispositionValue"
                  loading={isDispositionsLoading}
                  onButtonCommand={handleButtonCommand}
                  onRowMoved={handleRowMoved}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CallCardManagementScreen;

